import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { openRazorpay } from '@/lib/razorpay';
import { toast } from 'sonner';
import { errMsg } from '@/lib/errors';

const SAMPLE_PLAN = [
  { meal: 'Morning Drink', items: ['Saffron Almond Drink', 'Fennel-Pear Cooler', 'Spiced Coconut Water'] },
  { meal: 'Breakfast', items: ['Paneer Avocado Toast', 'Savoury Millet Pancakes', 'Apple-Cinnamon Oats', 'Breakfast Tortilla Wrap'] },
  { meal: 'Mid-Morning', items: ['Roasted Makhana Mix', 'Date-Coconut Bites'] },
  { meal: 'Lunch', items: ['Tandoori Paneer Grain Bowl', 'Coconut Vegetable Rice Bowl', 'Mediterranean Khichdi Bowl', 'Paneer & Hummus Wrap'] },
  { meal: 'Snacks', items: ['Sweet Potato Chaat', 'Hummus Pita', 'Roasted Makhana Mix'] },
  { meal: 'Dinner', items: ['Herbed Paneer Kebabs', 'Pumpkin Pasta', 'Coconut Tofu Bowl', 'Mild Mexican Rice Bowl'] },
  { meal: 'Bedtime', items: ['Nutmeg Almond Drink', 'Saffron Milk'] },
];

const FEE = 2900;

const DietConsultation = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [days, setDays] = useState([]);
  const [activeDay, setActiveDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reason, setReason] = useState('');
  const [step, setStep] = useState('select'); // select | payment | confirmed
  const [processing, setProcessing] = useState(false);
  const [confirmedAppt, setConfirmedAppt] = useState(null);

  useEffect(() => {
    api.get('/consultations/available-slots?days=10').then(({ data }) => {
      setDays(data.days || []);
      if (data.days?.length) setActiveDay(data.days[0].date);
    });
  }, []);

  const proceedToPayment = async () => {
    if (!selectedSlot) { toast.error('Choose a time slot'); return; }
    if (!user) { nav(`/login?next=${encodeURIComponent('/consultation/diet')}`); return; }
    setStep('payment');
  };

  const payAndBook = async () => {
    setProcessing(true);
    try {
      let payment_id = null;
      try {
        const rp = await openRazorpay({
          amount: FEE,
          purpose: 'diet',
          description: 'Personalised Diet Consultation',
          name: user?.name,
          email: user?.email,
        });
        payment_id = rp.payment_id;
      } catch (e) {
        if (!/not configured/i.test(String(e?.message || ''))) throw e;
      }
      const { data: booking } = await api.post('/consultations/book', {
        slot_datetime: selectedSlot,
        type: 'diet',
        reason: reason || null,
        payment_id,
      });
      setConfirmedAppt(booking.appointment);
      setStep('confirmed');
    } catch (e) {
      if (e?.message === 'Payment cancelled') toast.message('Payment cancelled');
      else toast.error(errMsg(e, 'Booking failed'));
    } finally { setProcessing(false); }
  };

  const activeDayObj = days.find((d) => d.date === activeDay);

  return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container max-w-5xl">
          <div className="sans-label mb-6">Personalised Diet Consultation</div>
          <h1 className="sans-h1">Eat for your body, not a template.</h1>
          <p className="sans-body mt-6 max-w-2xl">
            A private 30–40 minute video consultation with a BAMS-qualified physician. Your personalised diet is then
            hand-prepared for you and delivered to your dashboard within 3–5 days.
          </p>
          <div className="mt-4 text-sm text-sans_mute italic">Personally prepared by a specialist — not an AI-generated diet chart.</div>

          <div className="mt-12 flex items-baseline gap-4">
            <div className="font-serif text-4xl text-sans_ink">₹{FEE.toLocaleString('en-IN')}</div>
            <div className="text-sans_mute">Consultation + Personalised Diet</div>
          </div>

          {step === 'select' && (
            <>
              {/* Sample plan (demo) */}
              <div className="mt-20">
                <div className="sans-label mb-2 text-emerald-800">Sample plan — for demonstration only</div>
                <h2 className="font-serif text-3xl text-sans_ink">See what your diet could look like</h2>
                <div className="mt-8 border border-sans_line bg-white overflow-hidden">
                  {SAMPLE_PLAN.map((row, i) => (
                    <div key={row.meal} className={`grid md:grid-cols-[180px_1fr] gap-6 p-6 ${i > 0 ? 'border-t border-sans_line' : ''}`}>
                      <div className="font-serif text-lg text-sans_ink">{row.meal}</div>
                      <div className="flex flex-wrap gap-2">
                        {row.items.map((item) => (
                          <span key={item} className="text-sm text-sans_ink border border-sans_line bg-sans_bone px-3 py-1.5">{item}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs text-sans_mute italic max-w-2xl">Your actual plan will be personalised for you. Every Saans diet plan is personally prepared by a specialist after your consultation. The dishes shown above are only examples.</p>
              </div>

              {/* Booking — placed BELOW the demo plan */}
              <div className="mt-20">
                <div className="sans-label mb-6">Book your consultation</div>
                <h2 className="font-serif text-3xl text-sans_ink">Choose a date &amp; time</h2>

                <div className="mt-8 flex gap-3 overflow-x-auto no-scrollbar pb-2">
                  {days.map((d) => (
                    <button
                      key={d.date}
                      data-testid={`diet-day-${d.date}`}
                      onClick={() => { setActiveDay(d.date); setSelectedSlot(null); }}
                      className={`flex-shrink-0 px-5 py-4 border transition-colors ${
                        activeDay === d.date ? 'bg-sans_ink text-sans_bone border-sans_ink' : 'bg-white text-sans_ink border-sans_line hover:border-sans_ink'
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{d.day_label}</div>
                      <div className="font-serif text-lg mt-1">{d.date_label}</div>
                    </button>
                  ))}
                </div>

                <div className="mt-10 sans-label mb-4">Available times</div>
                {activeDayObj && (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {activeDayObj.slots.map((s) => (
                      <button
                        key={s.datetime}
                        disabled={!s.available}
                        data-testid={`diet-slot-${s.datetime}`}
                        onClick={() => setSelectedSlot(s.datetime)}
                        className={`py-3.5 text-sm tracking-wide border transition-colors ${
                          !s.available
                            ? 'bg-sans_line/40 text-sans_mute line-through border-transparent cursor-not-allowed'
                            : selectedSlot === s.datetime
                              ? 'bg-sans_ink text-sans_bone border-sans_ink'
                              : 'bg-white text-sans_ink border-sans_line hover:border-sans_ink'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-10">
                  <label className="sans-label block mb-3">Anything you'd like your specialist to know? (optional)</label>
                  <textarea
                    data-testid="diet-reason-input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="e.g. gut discomfort, weight management goals, food allergies…"
                    className="w-full bg-white border border-sans_line p-4 text-sm text-sans_ink placeholder:text-sans_mute focus:outline-none focus:border-sans_ink transition-colors"
                  />
                </div>

                {/* Fee + CTA summary */}
                <div className="mt-10 border border-sans_line bg-white p-6 md:p-8 flex flex-wrap items-center justify-between gap-6">
                  <div>
                    <div className="sans-label mb-2">Summary</div>
                    <div className="text-sm text-sans_mute">
                      {selectedSlot
                        ? new Date(selectedSlot).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' })
                        : 'Select a date and time above'}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-[0.22em] text-sans_mute">Total</div>
                      <div className="font-serif text-2xl text-sans_ink">₹{FEE.toLocaleString('en-IN')}</div>
                    </div>
                    <button
                      onClick={proceedToPayment}
                      data-testid="diet-continue-btn"
                      disabled={!selectedSlot || processing}
                      className="sans-btn-primary disabled:opacity-40"
                    >
                      Continue to payment
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-xs text-sans_mute leading-relaxed">Free cancellation up to 12 hours before your slot.</p>
              </div>
            </>
          )}

          {step === 'payment' && (
            <div className="mt-16 max-w-xl mx-auto bg-white border border-sans_line p-10">
              <div className="sans-label mb-4">Payment</div>
              <h2 className="font-serif text-3xl text-sans_ink">Confirm your diet consultation</h2>
              <div className="mt-8 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-sans_mute">When</span><span className="text-sans_ink">{new Date(selectedSlot).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>
                <div className="flex justify-between"><span className="text-sans_mute">Type</span><span className="text-sans_ink">Diet Consultation</span></div>
                <div className="border-t border-sans_line pt-3 flex justify-between font-serif text-lg"><span className="text-sans_ink">Total</span><span className="text-sans_ink">₹{FEE.toLocaleString('en-IN')}</span></div>
              </div>
              <div className="mt-6 p-4 bg-sans_bone border border-sans_line text-xs text-sans_mute leading-relaxed">
                <strong className="text-sans_ink">Secure payment via Razorpay</strong> — UPI, cards, netbanking &amp; wallets.
              </div>
              <button onClick={payAndBook} disabled={processing} data-testid="diet-pay-btn" className="sans-btn-primary w-full mt-8">
                {processing ? 'Processing…' : `Pay ₹${FEE.toLocaleString('en-IN')} & confirm`}
              </button>
              <button onClick={() => setStep('select')} className="w-full mt-4 text-sm text-sans_mute hover:text-sans_ink">← Back</button>
            </div>
          )}

          {step === 'confirmed' && confirmedAppt && (
            <div className="mt-16 max-w-2xl mx-auto text-center">
              <div className="sans-label mb-6">Confirmed</div>
              <h2 className="sans-h1">Your diet consultation is booked.</h2>
              <div className="sans-rule mx-auto mt-8" />
              <div className="mt-12 bg-white border border-sans_line p-10 text-left">
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between"><span className="text-sans_mute">When</span><span className="text-sans_ink">{new Date(confirmedAppt.slot_datetime).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' })}</span></div>
                  <div className="flex justify-between items-center"><span className="text-sans_mute">Meeting link</span><a href={confirmedAppt.meet_link} className="sans-link text-sans_ink text-right break-all">{confirmedAppt.meet_link}</a></div>
                </div>
                <p className="mt-8 text-xs text-sans_mute leading-relaxed">
                  A confirmation with the meeting link has been sent to your email. After your consultation, your personalised diet plan will
                  appear in <strong>My Diet Chart</strong> within 3–5 days.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <button onClick={() => nav('/dashboard')} className="sans-btn-primary">View my dashboard</button>
                <button onClick={() => nav('/')} className="sans-btn-secondary">Return home</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DietConsultation;
